/**
 * Translation Helpers
 * -----------------------------------------------------------------------------
 * A collection of functions to handle variant shipping note translations
 * for localized markets. This is an interim solution to work around Smartling
 * translation limitations for variant metafields.
 *
 * Current contents:
 * - translateShippingNote - Translates shipping notes based on locale mappings
 */

// French month translations
const FRENCH_MONTHS = {
  january: "janvier",
  february: "février",
  march: "mars",
  april: "avril",
  may: "mai",
  june: "juin",
  july: "juillet",
  august: "août",
  september: "septembre",
  october: "octobre",
  november: "novembre",
  december: "décembre",
} as const;

/**
 * Get the current locale from Shopify global object or DOM
 * @returns The current locale code (e.g., 'FR', 'US', 'GB')
 */
const getCurrentLocale = (): string => {
  if (typeof window !== "undefined" && window.Shopify?.locale) {
    return window.Shopify.locale.toUpperCase();
  }

  const htmlLocale = document.documentElement.getAttribute("data-country-code");
  if (htmlLocale) {
    return htmlLocale;
  }

  return "US";
};

/**
 * Translate dynamic shipping date patterns to French
 * @param originalNote - The original shipping note in English
 * @returns Translated note or original if no pattern matches
 */
const translateShippingNoteToFrench = (originalNote: string): string => {
  const lowerNote = originalNote.toLowerCase().trim();

  // Handle "sold out"
  if (lowerNote === "sold out") {
    return "Épuisé";
  }

  // Pattern: "ships by [month] [day]th/st/nd/rd" → "Expédition avant le [day] [month]"
  const shipsByDateMatch = lowerNote.match(
    /^ships by (\w+) (\d+)(?:st|nd|rd|th)$/
  );
  if (shipsByDateMatch) {
    const [, month, day] = shipsByDateMatch;
    const frenchMonth = FRENCH_MONTHS[month as keyof typeof FRENCH_MONTHS];
    if (frenchMonth) {
      return `Expédition avant le ${day} ${frenchMonth}`;
    }
  }

  // Pattern: "ships by end of [month]" → "Expédition prévue avant la fin du mois de [month]."
  const shipsByEndOfMonthMatch = lowerNote.match(/^ships by end of (\w+)$/);
  if (shipsByEndOfMonthMatch) {
    const [, month] = shipsByEndOfMonthMatch;
    const frenchMonth = FRENCH_MONTHS[month as keyof typeof FRENCH_MONTHS];
    if (frenchMonth) {
      return `Expédition prévue avant la fin du mois de ${frenchMonth}.`;
    }
  }

  // Pattern: "ships by [month]" → "Expédition en [month]"
  const shipsByMonthMatch = lowerNote.match(/^ships by (\w+)$/);
  if (shipsByMonthMatch) {
    const [, month] = shipsByMonthMatch;
    const frenchMonth = FRENCH_MONTHS[month as keyof typeof FRENCH_MONTHS];
    if (frenchMonth) {
      return `Expédition en ${frenchMonth}`;
    }
  }

  return originalNote;
};

/**
 * Translate shipping note text based on current locale
 * @param originalNote - The original shipping note text in English
 * @param targetLocale - Optional target locale override
 * @returns Translated shipping note or original text if no translation found
 */
export const translateShippingNote = (
  originalNote: string | null | undefined,
  targetLocale?: string
): string | null => {
  if (!originalNote) return null;

  const locale = targetLocale || getCurrentLocale();

  // Only French market needs translation
  if (locale === "FR") {
    return translateShippingNoteToFrench(originalNote);
  }

  return originalNote;
};
